/**
 * Case Scope Tab
 *
 * Living document workspace for IR analysts.
 * Contains the scope document, investigation notes, and version history.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  NotesSection,
  ScopeDocument,
  ScopeErrorState,
  ScopeHeader,
  ScopeLoadingState,
  VersionHistoryPanel,
} from './case-scope/case-scope-tab.parts'
import { useCaseScopeTab } from './case-scope/use-case-scope-tab'

interface CaseScopeTabProps {
  caseId: string
}

type ScopeTabState = ReturnType<typeof useCaseScopeTab>

interface ScopeBodyProps {
  state: ScopeTabState
  t: TFunction<'cases'>
}

function ScopeBody({ state, t }: ScopeBodyProps): ReactElement {
  const submitNote = (): void => {
    void (state.editingNoteId ? state.handleUpdateNote() : state.handleCreateNote())
  }
  return (
    <div className="space-y-6">
      <ScopeHeader
        scope={state.scope}
        showHistory={state.showHistory}
        onToggleHistory={() => {
          state.setShowHistory(!state.showHistory)
        }}
        onRefetch={() => {
          void state.refetch()
        }}
        t={t}
      />
      {state.showHistory && (
        <VersionHistoryPanel
          versions={state.versions}
          loadingVersions={state.loadingVersions}
          currentVersion={state.scope?.currentVersion}
          t={t}
        />
      )}
      <ScopeDocument
        scope={state.scope}
        isEditing={state.isEditing}
        editContent={state.editContent}
        setEditContent={state.setEditContent}
        changeSummary={state.changeSummary}
        setChangeSummary={state.setChangeSummary}
        saving={state.saving}
        onEdit={() => {
          state.setIsEditing(true)
        }}
        onCancel={state.handleCancel}
        onSave={() => {
          void state.handleSave()
        }}
        t={t}
      />
      <NotesSection
        notes={state.notes}
        notesTotal={state.notesTotal}
        loadingNotes={state.loadingNotes}
        showNoteForm={state.showNoteForm}
        noteFormProps={{
          noteContent: state.noteContent,
          setNoteContent: state.setNoteContent,
          noteTags: state.noteTags,
          toggleTag: state.toggleTag,
          editingNoteId: state.editingNoteId,
          savingNote: state.savingNote,
          onCancel: state.resetNoteForm,
          onSubmit: submitNote,
        }}
        onAddNote={() => {
          state.setEditingNoteId(null)
          state.setNoteContent('')
          state.setShowNoteForm(true)
        }}
        onEditNote={state.startEditNote}
        onDeleteNote={(id) => {
          void state.handleDeleteNote(id)
        }}
        t={t}
      />
    </div>
  )
}

export function CaseScopeTab({ caseId }: CaseScopeTabProps): ReactElement {
  const { t } = useTranslation('cases')
  const state = useCaseScopeTab({ caseId, t })

  if (state.loading) return <ScopeLoadingState t={t} />
  if (state.error)
    return (
      <ScopeErrorState
        error={state.error}
        onRetry={() => {
          void state.refetch()
        }}
        t={t}
      />
    )

  return <ScopeBody state={state} t={t} />
}
